import type {
  BodyBlock,
  Group,
  GroupKind,
  PreambleNode,
  Statute,
  Unit,
  UnitKind,
} from '../types';

/**
 * v2 civils-suits adapter (CPC 1908, Contract Act, Specific Relief, TOPA,
 * Limitation Act, QSO 1984). Walks the v2 `structure` tree depth-first and
 * emits a flat list of `Group`s, each containing `Unit`s.
 *
 * Container types: preamble, preliminary, part, chapter, heading, schedule,
 * appendix, order, division.
 * Leaf (unit) types: section, article, rule, form.
 *
 * Adapter is tolerant: missing ids are synthesized; non-string text is coerced;
 * unknown node types are skipped (warned in dev).
 */

type AnyRecord = Record<string, unknown>;

const isObject = (v: unknown): v is AnyRecord =>
  v !== null && typeof v === 'object' && !Array.isArray(v);

const asString = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));

const trimOrEmpty = (v: unknown): string => asString(v).trim();

const CONTAINER_TYPES = new Set([
  'preamble',
  'preliminary',
  'part',
  'chapter',
  'heading',
  'schedule',
  'appendix',
  'order',
  'division',
]);

const LEAF_TYPES = new Set(['section', 'article', 'rule', 'form']);

function devWarn(message: string, ...rest: unknown[]) {
  if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(`[v2CivilsSuitsAdapter] ${message}`, ...rest);
  }
}

function pickGroupKind(type: string): GroupKind {
  switch (type) {
    case 'preliminary':
      return 'preliminary';
    case 'part':
      return 'part';
    case 'chapter':
      return 'chapter';
    case 'order':
      return 'order';
    case 'schedule':
      return 'schedule';
    case 'appendix':
      return 'appendix';
    case 'division':
      return 'division';
    default:
      return 'other';
  }
}

function pickUnitKind(type: string): UnitKind {
  switch (type) {
    case 'article':
      return 'article';
    case 'rule':
      return 'rule';
    case 'form':
      return 'form';
    default:
      return 'section';
  }
}

function unitNumberFromLabel(label: string, fallback: string): string {
  const t = label.trim();
  let m = t.match(/^Section\s+([A-Za-z0-9]+)$/i);
  if (m) return m[1];
  m = t.match(/^Article\s+([A-Za-z0-9]+)$/i);
  if (m) return m[1];
  m = t.match(/^Rule\s+([A-Za-z0-9]+)$/i);
  if (m) return m[1];
  m = t.match(/^Form\s+([A-Za-z0-9]+)$/i);
  if (m) return m[1];
  m = t.match(/([A-Za-z0-9]+)\s*$/);
  if (m) return m[1];
  return fallback;
}

function unitNumberDisplay(kind: UnitKind, number: string, label: string): string {
  if (label && label.trim()) return label.trim();
  switch (kind) {
    case 'article':
      return `Article ${number}`;
    case 'rule':
      return `Rule ${number}`;
    case 'form':
      return `Form ${number}`;
    default:
      return number;
  }
}

function stripParenLabel(label: string): string {
  const t = label.trim();
  const m = t.match(/^\(([^)]+)\)$/);
  return m ? m[1] : t;
}

function mapClause(node: AnyRecord, idx: number, parentId: string): BodyBlock {
  const label = trimOrEmpty(node.label);
  const id = trimOrEmpty(node.id) || `${parentId}-cl-${idx}`;
  const letter = stripParenLabel(label) || String(idx + 1);
  let text = trimOrEmpty(node.text);
  const subClauses = Array.isArray(node.subClauses) ? (node.subClauses as AnyRecord[]) : [];
  if (subClauses.length) {
    const tail = subClauses
      .map((sc) => trimOrEmpty(sc?.text))
      .filter(Boolean)
      .join('\n');
    if (tail) text = text ? `${text}\n${tail}` : tail;
  }
  // Anchor id is captured implicitly via parent unit; clause body is opaque text.
  void id;
  return { kind: 'clause', letter, text };
}

function mapProviso(node: unknown): BodyBlock | null {
  if (typeof node === 'string') {
    const t = node.trim();
    return t ? { kind: 'proviso', text: t } : null;
  }
  if (isObject(node)) {
    const lab = trimOrEmpty(node.label);
    const text = trimOrEmpty(node.text);
    const merged = [lab, text].filter(Boolean).join(' ').trim();
    return merged ? { kind: 'proviso', text: merged } : null;
  }
  return null;
}

function mapExplanation(node: unknown): BodyBlock | null {
  if (typeof node === 'string') {
    const t = node.trim();
    return t ? { kind: 'explanation', text: t } : null;
  }
  if (isObject(node)) {
    const lab = trimOrEmpty(node.label);
    const text = trimOrEmpty(node.text);
    const merged = [lab, text].filter(Boolean).join(': ').trim();
    return merged ? { kind: 'explanation', text: merged } : null;
  }
  return null;
}

function mapIllustration(node: unknown): BodyBlock | null {
  if (typeof node === 'string') {
    const t = node.trim();
    return t ? { kind: 'illustration', text: t } : null;
  }
  if (isObject(node)) {
    const text = trimOrEmpty(node.text) || trimOrEmpty(node.content) || trimOrEmpty(node.label);
    return text ? { kind: 'illustration', text } : null;
  }
  return null;
}

function mapSubsection(node: AnyRecord, idx: number, parentId: string): BodyBlock {
  const label = trimOrEmpty(node.label);
  const number = stripParenLabel(label) || String(idx + 1);
  const text = trimOrEmpty(node.text);
  const subId = trimOrEmpty(node.id) || `${parentId}-ss-${idx}`;
  void subId;

  const children: BodyBlock[] = [];
  const clauses = Array.isArray(node.clauses) ? (node.clauses as AnyRecord[]) : [];
  clauses.forEach((c, i) => children.push(mapClause(c, i, parentId)));
  const provisos = Array.isArray(node.provisos) ? node.provisos : [];
  provisos.forEach((p) => {
    const block = mapProviso(p);
    if (block) children.push(block);
  });
  const explanations = Array.isArray(node.explanations) ? node.explanations : [];
  explanations.forEach((e) => {
    const block = mapExplanation(e);
    if (block) children.push(block);
  });

  return {
    kind: 'subsection',
    number,
    text,
    ...(children.length ? { children } : {}),
  };
}

function mapUnitBody(node: AnyRecord, unitId: string): BodyBlock[] {
  const blocks: BodyBlock[] = [];

  const subsRaw = node.subsections ?? node.sub_sections ?? node.subSections;
  const subsections = Array.isArray(subsRaw) ? (subsRaw as AnyRecord[]) : [];

  if (subsections.length === 0) {
    const fullText = trimOrEmpty(node.fullText) || trimOrEmpty(node.text) || trimOrEmpty(node.content);
    if (fullText) blocks.push({ kind: 'paragraph', text: fullText });
  } else {
    subsections.forEach((sub, i) => blocks.push(mapSubsection(sub, i, unitId)));
  }

  const provisosTop = Array.isArray(node.provisos) ? node.provisos : [];
  provisosTop.forEach((p) => {
    const block = mapProviso(p);
    if (block) blocks.push(block);
  });

  const explanationsTop = Array.isArray(node.explanations) ? node.explanations : [];
  explanationsTop.forEach((e) => {
    const block = mapExplanation(e);
    if (block) blocks.push(block);
  });

  const illustrations = Array.isArray(node.illustrations) ? node.illustrations : [];
  illustrations.forEach((il) => {
    const block = mapIllustration(il);
    if (block) blocks.push(block);
  });

  return blocks;
}

function nodeIsLeaf(node: AnyRecord): boolean {
  const t = trimOrEmpty(node.type);
  if (LEAF_TYPES.has(t)) return true;
  if (CONTAINER_TYPES.has(t)) return false;
  return false;
}

function structuralHeading(node: AnyRecord, fallback: string): string {
  const label = trimOrEmpty(node.label);
  const title = trimOrEmpty(node.title);
  // Plain space separator preserves compatibility with the existing
  // splitPartHeading / splitChapterHeading regexes used by the navigator.
  if (label && title && label.toLowerCase() !== title.toLowerCase()) return `${label} ${title}`;
  if (label) return label;
  if (title) return title;
  return fallback;
}

function makeUnit(node: AnyRecord, fallbackOrder: number): Unit | null {
  const type = trimOrEmpty(node.type) || 'section';
  if (!LEAF_TYPES.has(type)) {
    devWarn(`Unrecognized leaf type "${type}" — coercing to section`, node);
  }
  const kind = pickUnitKind(type);
  const id = trimOrEmpty(node.id) || `${type || 'leaf'}-${fallbackOrder}`;
  const label = trimOrEmpty(node.label);
  const title = trimOrEmpty(node.title) || label;
  const number = unitNumberFromLabel(label, String(fallbackOrder));
  const numberDisplay = unitNumberDisplay(kind, number, label);
  const body = mapUnitBody(node, id);
  return {
    id,
    kind,
    number,
    numberDisplay,
    title,
    body,
  };
}

function buildPreamble(node: AnyRecord): PreambleNode | undefined {
  const id = trimOrEmpty(node.id) || 'preamble';
  const titleField = trimOrEmpty(node.title) || trimOrEmpty(node.label) || 'Preamble';

  const body: BodyBlock[] = [];

  const directText = trimOrEmpty(node.text);
  if (directText) body.push({ kind: 'paragraph', text: directText });

  const children = Array.isArray(node.children) ? (node.children as AnyRecord[]) : [];
  children.forEach((child) => {
    if (!isObject(child)) return;
    body.push(...mapUnitBody(child, id));
  });

  if (body.length === 0) {
    body.push({ kind: 'paragraph', text: titleField });
  }

  return { id, title: titleField, body };
}

function isLeafBearing(container: AnyRecord): boolean {
  const children = Array.isArray(container.children) ? (container.children as unknown[]) : [];
  return children.some((c) => isObject(c) && nodeIsLeaf(c));
}

function buildStatute(raw: AnyRecord, hint?: string): Statute {
  const metadata = isObject(raw.metadata) ? (raw.metadata as AnyRecord) : {};
  const title = trimOrEmpty(metadata.title) || 'Untitled Statute';
  const shortCode = trimOrEmpty(metadata.shortTitle) || trimOrEmpty(metadata.shortCode) || title;
  const yearRaw = metadata.year;
  const year = typeof yearRaw === 'number' && !Number.isNaN(yearRaw) ? yearRaw : 0;
  const jurisdiction = trimOrEmpty(metadata.jurisdiction) || 'PK';
  const category = hint || trimOrEmpty(metadata.category) || 'civils-suits';

  const structure = Array.isArray(raw.structure) ? (raw.structure as AnyRecord[]) : [];
  const groups: Group[] = [];
  let preamble: PreambleNode | undefined;
  let groupSeq = 0;
  let unitOrderSeq = 0;

  const visit = (node: AnyRecord, ancestors: string[]) => {
    if (!isObject(node)) return;
    const type = trimOrEmpty(node.type);

    if (type === 'preamble') {
      if (!preamble) preamble = buildPreamble(node);
      return;
    }

    if (LEAF_TYPES.has(type)) {
      // Stray leaf at the top level — wrap in an "other" group, collapsing
      // adjacent leaves with the same ancestor signature.
      const u = makeUnit(node, unitOrderSeq++);
      if (!u) return;
      const sig = ancestors.join('|');
      const lastGroup = groups[groups.length - 1];
      const lastSig = lastGroup?.ancestors?.join('|') ?? '';
      if (lastGroup && lastGroup.kind === 'other' && lastSig === sig) {
        lastGroup.units.push(u);
      } else {
        groups.push({
          id: `grp-${groupSeq++}`,
          kind: 'other',
          label: ancestors[ancestors.length - 1] || 'Sections',
          units: [u],
          ...(ancestors.length ? { ancestors: ancestors.slice(0, -1) } : {}),
        });
      }
      return;
    }

    if (CONTAINER_TYPES.has(type)) {
      const groupKind = pickGroupKind(type);
      const label = trimOrEmpty(node.label) || (type ? type[0].toUpperCase() + type.slice(1) : 'Section');
      const titleField = trimOrEmpty(node.title);
      const groupAncestors = ancestors.slice();

      if (isLeafBearing(node)) {
        const units: Unit[] = [];
        const children = Array.isArray(node.children) ? (node.children as AnyRecord[]) : [];
        children.forEach((child) => {
          if (!isObject(child)) return;
          if (LEAF_TYPES.has(trimOrEmpty(child.type))) {
            const u = makeUnit(child, unitOrderSeq++);
            if (u) units.push(u);
          }
        });

        if (units.length > 0) {
          const id = trimOrEmpty(node.id) || `grp-${type}-${groupSeq++}`;
          groups.push({
            id,
            kind: groupKind,
            label,
            ...(titleField ? { title: titleField } : {}),
            ...(groupAncestors.length ? { ancestors: groupAncestors } : {}),
            units,
          });
        }

        // Recurse for nested containers (e.g. schedule that also has order children).
        const trail = [...ancestors, structuralHeading(node, label)];
        children.forEach((child) => {
          if (!isObject(child)) return;
          if (CONTAINER_TYPES.has(trimOrEmpty(child.type))) visit(child, trail);
        });
        return;
      }

      const trail = [...ancestors, structuralHeading(node, label)];
      const children = Array.isArray(node.children) ? (node.children as AnyRecord[]) : [];
      children.forEach((c) => visit(c as AnyRecord, trail));
      return;
    }

    if (type) {
      devWarn(`Unrecognized container type "${type}" — descending into children`, node);
    }
    const children = Array.isArray(node.children) ? (node.children as AnyRecord[]) : [];
    children.forEach((c) => visit(c as AnyRecord, ancestors));
  };

  structure.forEach((node) => visit(node, []));

  const idHint = hint || trimOrEmpty(metadata.shortTitle) || trimOrEmpty(metadata.title) || 'statute';
  const id = idHint.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'statute';

  return {
    id,
    title,
    shortCode,
    year,
    jurisdiction,
    category,
    ...(preamble ? { preamble } : {}),
    groups,
    metadata,
  };
}

export function adaptStatute(rawJson: unknown, hint?: string): Statute {
  if (!isObject(rawJson)) {
    devWarn('adaptStatute received non-object input', rawJson);
    return {
      id: 'unknown',
      title: '',
      shortCode: '',
      year: 0,
      jurisdiction: 'PK',
      category: hint || 'civils-suits',
      groups: [],
      metadata: {},
    };
  }
  return buildStatute(rawJson, hint);
}
